"use client"

import { ScrambleButton } from "@/components/custom/scramble-animation/scramble-button"
import { logger } from "@/lib/logger"
import { cn, nbsp } from "@/lib/utils"
import { useEffect, useRef, useState } from "react"
import { ScrollingArrow } from "./components/scrolling-arrow"

const VIDEOS_LINKS = {
    ANIMATION_START_WEBM:
        "https://7xrjrl2xcf.ufs.sh/f/0jT98Fb5G1RoDmDOliNAHO8c9B5Phx3U4WvZmkrbfL1NFs2p",
    ANIMATION_START_MP4:
        "https://7xrjrl2xcf.ufs.sh/f/0jT98Fb5G1Ror6mOA46DExaTusW3VL58kQ9cDpARz2FPN4gX",
    ANIMATION_CYCLE_WEBM:
        "https://7xrjrl2xcf.ufs.sh/f/0jT98Fb5G1RoPGNCs8g4KOJycgpkPTGnNRxHsdwVh63LAMoQ",
    ANIMATION_CYCLE_MP4:
        "https://7xrjrl2xcf.ufs.sh/f/0jT98Fb5G1Roq1L1up08d5PlRkVWsnA7DQJHoF4M9bZzitxK",
}

function HeroVideo({ className }: { className?: string }) {
    const [videosLoaded, setVideosLoaded] = useState(false)
    const [isFading, setIsFading] = useState(false)
    const introVideoRef = useRef<HTMLVideoElement>(null)
    const loopVideoRef = useRef<HTMLVideoElement>(null)

    useEffect(() => {
        const introVideo = introVideoRef.current
        const loopVideo = loopVideoRef.current
        if (!introVideo || !loopVideo) return

        const onTimeUpdate = () => {
            if (introVideo.duration - introVideo.currentTime <= 0.3) {
                setIsFading(true)
                introVideo.removeEventListener("timeupdate", onTimeUpdate)
            }
        }

        const checkVideoReady = (video: HTMLVideoElement) => {
            return new Promise((resolve) => {
                // If video is already ready, resolve immediately
                if (video.readyState >= 3) {
                    resolve(true)
                } else {
                    video.addEventListener("loadeddata", () => resolve(true), {
                        once: true,
                    })
                }
            })
        }

        Promise.all([checkVideoReady(introVideo), checkVideoReady(loopVideo)])
            .then(() => {
                setVideosLoaded(true)
                introVideo
                    .play()
                    .catch((e) => logger.error("Intro video play error", e))
                loopVideo
                    .play()
                    .catch((e) => logger.error("Loop video play error", e))
                introVideo.addEventListener("timeupdate", onTimeUpdate)
            })
            .catch((error) => {
                logger.error("Error loading videos:", error)
            })

        return () => {
            introVideo.removeEventListener("timeupdate", onTimeUpdate)
        }
    }, [])

    return (
        <div
            className={cn(
                "overflow-hidden transition-opacity duration-500",
                {
                    "opacity-0": !videosLoaded,
                    "opacity-100": videosLoaded,
                },
                className,
            )}
        >
            <video
                ref={introVideoRef}
                autoPlay
                playsInline
                muted
                disableRemotePlayback
                preload="auto"
                className={cn("absolute inset-0 h-full w-full object-contain")}
            >
                <source
                    src={VIDEOS_LINKS.ANIMATION_START_MP4}
                    type="video/mp4"
                />
                {/* <source
                    src={VIDEOS_LINKS.ANIMATION_START_WEBM}
                    type="video/webm"
                /> */}
            </video>
            <video
                ref={loopVideoRef}
                autoPlay
                loop
                playsInline
                muted
                disableRemotePlayback
                preload="auto"
                className={cn(
                    "absolute inset-0 h-full w-full object-contain transition-opacity duration-300",
                    {
                        "opacity-100": isFading,
                        "opacity-0": !isFading,
                    },
                )}
            >
                <source
                    src={VIDEOS_LINKS.ANIMATION_CYCLE_MP4}
                    type="video/mp4"
                />
                <source
                    src={VIDEOS_LINKS.ANIMATION_CYCLE_WEBM}
                    type="video/webm"
                />
            </video>
        </div>
    )
}

export function HeroSection() {
    return (
        <section className="h-[calc(100vh-80px)] w-full lg:h-[calc(100vh-var(--header-height))]">
            <div className="flex h-full w-full flex-col px-4 lg:px-11">
                <div className="relative flex flex-1 flex-col items-center justify-start gap-4 py-10 lg:flex-row lg:justify-between lg:py-0">
                    <h2 className="hidden text-start text-[90px] leading-[1] tracking-tighter lg:block">
                        {nbsp`Gas-Powered `}
                        <br className="hidden lg:block" />
                        {nbsp`Meme Coin `}
                        <br className="hidden lg:block" />
                        {nbsp`Community `}
                    </h2>
                    <h2 className="text-center text-3xl md:text-4xl lg:hidden">
                        Gas-Powered Meme Coin Community
                    </h2>
                    <p className="max-w-[70ch] text-center text-base leading-7 lg:max-w-[50ch] lg:text-start">
                        <strong className="font-bold">$FartHub</strong> is where{" "}
                        {nbsp`legendary meme`} culture meets{" "}
                        {nbsp`premium execution`}. {nbsp`We're building`}{" "}
                        {nbsp`a home`} {nbsp`for the community,`} backed by{" "}
                        {nbsp`a transparent team`} and {nbsp`a commitment`}{" "}
                        {nbsp`to making`} this the most iconic brand{" "}
                        {nbsp`in the degen space.`}
                    </p>
                    <HeroVideo className="absolute bottom-0 left-1/2 z-[-1] aspect-square w-[80%] -translate-x-1/2 lg:w-[37%]" />
                </div>
                <div className="border-border flex h-15 w-full items-center justify-between border-t">
                    <ScrambleButton
                        onClick={() => {
                            window.scrollTo({
                                top: window.innerHeight,
                                behavior: "smooth",
                            })
                        }}
                        text="Scroll to discover"
                        className="text-xs"
                        uppercase
                    />
                    <ScrollingArrow
                        onClick={() => {
                            window.scrollTo({
                                top: window.innerHeight,
                                behavior: "smooth",
                            })
                        }}
                    />
                </div>
            </div>
        </section>
    )
}
